import { useEffect, useRef, useState } from 'react';
import { LogOut, Menu, Search, ShieldCheck, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import type { Me, TenantSummary } from '@iot-dashboard/shared';
import { useAuth } from '../auth/AuthProvider';
import { DEMO_MODE } from '../config';
import { ThemeToggle } from '../components/ThemeToggle';

interface Props {
  me: Me;
  tenant?: TenantSummary;
  mspName: string;
  onToggleSidebar: () => void;
  drawerOpen?: boolean;
}

const ROLE_LABEL: Record<string, string> = {
  'msp-admin': 'MSP administrator',
  'tenant-admin': 'Administrator',
  'tenant-viewer': 'Read-only',
};

export function TopBar({ me, tenant, mspName, onToggleSidebar, drawerOpen = false }: Props) {
  const auth = useAuth();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setMenu(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const initials = (me.email || '?').slice(0, 2).toUpperCase();

  return (
    <header className="topbar">
      <Link to={me.isMsp ? '/msp' : '/'} className="topbar__brand">
        <span className="topbar__logo" aria-hidden="true" />
        <span>
          {mspName}
          <small>IoT dashboard</small>
        </span>
      </Link>
      <button
        type="button"
        className="btn btn--ghost btn--icon hamburger"
        onClick={onToggleSidebar}
        aria-label={drawerOpen ? 'Close navigation' : 'Open navigation'}
        aria-expanded={drawerOpen}
      >
        {drawerOpen ? <X size={20} /> : <Menu size={20} />}
      </button>
      {tenant ? (
        <form
          className="topbar__search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            navigate(`/t/${tenant.tenantId}/sensors?q=${encodeURIComponent(q)}`);
          }}
        >
          <Search size={16} aria-hidden="true" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search sensors by name, MAC or tag" aria-label="Search sensors" />
        </form>
      ) : null}
      <div className="topbar__spacer" />
      <div className="topbar__links">
        {DEMO_MODE ? <span className="demo-badge">Demo</span> : null}
        <div className="topbar__user" ref={ref}>
          <button type="button" className="topbar__link" onClick={() => setMenu((m) => !m)} aria-haspopup="menu" aria-expanded={menu}>
            <span className="topbar__avatar" aria-hidden="true">
              {initials}
            </span>
            <span className="nowrap">{me.email}</span>
          </button>
          {menu ? (
            <div className="menu" role="menu">
              <div className="menu__header">
                <div className="cell-primary">{me.email}</div>
                <div className="small muted">{ROLE_LABEL[me.role] ?? me.role}</div>
              </div>
              <div className="menu__section">
                <span className="menu__section-label">Appearance</span>
                <ThemeToggle />
              </div>
              <Link to="/account" className="menu__item" role="menuitem" onClick={() => setMenu(false)}>
                <ShieldCheck size={16} /> Account &amp; security
              </Link>
              <button type="button" className="menu__item" role="menuitem" onClick={() => auth.signOut().then(() => navigate('/login'))}>
                <LogOut size={16} /> Sign out
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
